import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { LogIn, UserPlus, ArrowLeft, KeyRound } from 'lucide-react';

export function AuthForm() {
  type AuthMode = 'login' | 'signup' | 'forgotPassword' | 'resetPassword';
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signUp, signOut } = useAuth();
  const [updateRequired, setUpdateRequired] = useState(false);
  const navigate = useNavigate();
  const resetFlowRef = useRef(false);

  useEffect(() => {
    // Check if user was redirected due to version update
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('update') === 'true') {
      setError('A system update requires you to sign in again.');
      // Clear the URL parameter
      window.history.replaceState({}, document.title, '/auth');
    }
  }, []);

  useEffect(() => {
    if (!window.location.hash || window.location.hash.length <= 1) return;
    if (resetFlowRef.current) return; // Prevent duplicate processing
    resetFlowRef.current = true;

    const params = new URLSearchParams(window.location.hash.substring(1));
    const type = params.get('type');

    if (type === 'recovery' && !params.get('error')) {
      setMode('resetPassword');
    } else if (params.get('error')) {
      const errorMsg = params.get('error_description') || 'Invalid or expired reset link';
      setError(`${errorMsg}. Please request a new password reset link below.`);
      setMode('forgotPassword');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setMode('resetPassword');
        // Clear the URL hash
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Clear error when switching modes
    setError('');
    setSuccess('');
  }, [mode]);

  const handleBackToLogin = () => {
    // Clear any remaining URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);
    resetFlowRef.current = false; // Reset the flow ref
    setMode('login');
    setError('');
    setSuccess('');
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!password.trim()) {
        throw new Error('Please enter a password');
      }

      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password
      });
      
      if (updateError) {
        console.error('Password update error:', updateError);
        throw updateError;
      }

      // Force sign out and clear any session
      await supabase.auth.signOut();
      
      // Show success message
      setSuccess('Password updated successfully! Please sign in with your new password.');
      setPassword('');
      setConfirmPassword('');
      resetFlowRef.current = false;

      // Automatically redirect after 2 seconds
      setTimeout(() => {
        setMode('login');
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 2000);

    } catch (err) {
      console.error('Password reset error:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    resetFlowRef.current = false;

    try {
      if (!email.trim()) {
        throw new Error('Please enter your email address');
      }

      // Use the full URL with the current origin
      const redirectTo = `${window.location.origin}/auth`;

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });

      if (error) {
        console.error('Password reset email error:', error);
        throw error;
      }

      setSuccess(`Password reset instructions have been sent to ${email}. Please check your inbox and spam/junk folder. The email will come from no-reply@mail.sanocda.com.`);
      // Don't clear email - keep it visible so user knows where the email was sent
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err instanceof Error ? err.message : 'Failed to send reset instructions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await signIn(email, password);
        navigate('/app'); 
      } else {
        const { data, error: signUpError } = await signUp(email, password);
        
        if (signUpError) throw signUpError;

        // Create initial profile if we have a user
        if (data?.user) {
          const { error: profileError } = await supabase
            .from('profiles')
            .insert([{
              id: data.user.id,
              email: email
            }])
            .select()
            .single();

          if (profileError) {
            console.error('Error creating profile:', profileError);
          }

          // Create default email preferences (best-effort, ignore duplicate)
          supabase.from('email_preferences').insert({ user_id: data.user.id }).then(({ error }) => {
            if (error && error.code !== '23505') console.error('Error creating email preferences:', error);
          });

          // Auto sign in after signup
          await signIn(email, password);

          // Fire welcome email — best-effort, don't block navigation
          try {
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData.session?.access_token;
            if (token) {
              fetch('/api/send-welcome-email', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              }).catch(() => {/* intentionally ignored */});
            }
          } catch {/* intentionally ignored */}

          navigate('/app');
          return;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm">
      <form 
        onSubmit={
          mode === 'resetPassword'
            ? handleResetPassword 
            : mode === 'forgotPassword'
              ? handleForgotPassword 
              : handleSubmit
        } 
        className="space-y-6"
      >
        {mode === 'forgotPassword' ? (
          <div>
            <button
              type="button"
              onClick={handleBackToLogin}
              className="mb-4 flex items-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to login
            </button>
            {!success ? (
              <>
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Reset Password</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Enter your email address and we'll send you instructions to reset your password.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Check Your Email</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  We've sent password reset instructions to the email address below.
                </p>
              </>
            )}
          </div>
        ) : mode === 'resetPassword' ? (
          <div>
            <button
              type="button"
              onClick={handleBackToLogin}
              className="mb-4 flex items-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to login
            </button>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              <KeyRound className="inline-block w-6 h-6 mr-2" />
              Set New Password
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Please enter your new password below.
            </p>
          </div>
        ) : null}

        {mode !== 'resetPassword' && (
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              disabled={loading || (mode === 'forgotPassword' && !!success)}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        )}

        {(mode !== 'forgotPassword' || mode === 'resetPassword') && (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {mode === 'resetPassword' ? 'New Password' : 'Password'}
            </label>
            <input
              id="password"
              type="password"
              required
              disabled={loading}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
        )}

        {mode === 'resetPassword' && (
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              required
              disabled={loading}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
        )}

        {success && (
          <div className="rounded-md bg-green-50 p-4 dark:bg-green-900/50 border-2 border-green-200 dark:border-green-700">
            <p className="text-sm font-medium text-green-700 dark:text-green-200">{success}</p>
            {success.includes('Redirecting') && (
              <div className="mt-2 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700 dark:border-green-300" />
                <span className="text-sm text-green-700 dark:text-green-200">
                  Please wait...
                </span>
              </div>
            )}
            {mode === 'forgotPassword' && (
              <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-700">
                <p className="text-xs text-green-600 dark:text-green-300 mb-3">
                  <strong>Didn't receive the email?</strong>
                </p>
                <ul className="text-xs text-green-600 dark:text-green-300 space-y-1 list-disc list-inside">
                  <li>Check your spam/junk folder</li>
                  <li>Wait a few minutes for delivery</li>
                  <li>Make sure you entered the correct email address</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/50">
            <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            {error.includes('Invalid') && mode !== 'forgotPassword' && (
              <button
                type="button"
                onClick={() => {
                  setMode('forgotPassword');
                  setError('');
                }}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Request new reset link
              </button>
            )}
          </div>
        )}

        {/* Hide submit button when success message is shown in forgot password mode */}
        {!(mode === 'forgotPassword' && success) && (
          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 dark:hover:bg-blue-800 transition-colors"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white" />
                <span>Processing...</span>
              </div>
            ) : mode === 'resetPassword' ? (
              <>
                <KeyRound className="mr-2 h-4 w-4" />
                Reset Password
              </>
            ) : mode === 'forgotPassword' ? (
              'Send Reset Instructions'
            ) : mode === 'login' ? (
              <>
                <LogIn className="mr-2 h-4 w-4" />
                Sign In
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Sign Up
              </>
            )}
          </button>
        )}

        {mode !== 'resetPassword' && <div className="space-y-2">
          {/* Show 'Return to Login' button when success message is shown */}
          {mode === 'forgotPassword' && success ? (
            <button
              type="button"
              onClick={handleBackToLogin}
              className="w-full text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Return to Login
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                disabled={loading}
                className="w-full text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>

              {mode === 'login' && (
                <button
                  type="button"
                  onClick={() => setMode('forgotPassword')}
                  disabled={loading}
                  className="w-full text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  Forgot your password?
                </button>
              )}
            </>
          )}
        </div>}
      </form>
    </div>
  );
}